import { useEffect, useRef, useState } from "react";

/**
 * Hero-right auto-rotating stat panel. Bone card, charcoal ink, oxblood accent.
 * Replaces the old MembersCard — same slot in the hero, real information inside.
 *
 * - One highlight visible at a time
 * - Auto-cycles every 4500ms with a subtle fade-up
 * - Pauses when hovered
 * - Bottom-row pager dots for manual navigation
 */

const STATS = [
  {
    key: "catalog",
    overline: "In the catalog",
    display: null,        // headline slide — no numeric callout
    headline: "Bulk Research Kits",
    sub: "Lyophilized products for academic research, CROs, and independent laboratories.",
  },
  {
    key: "warehouses",
    overline: "Fulfillment",
    display: "2",
    tail: "U.S. warehouses",
    sub: "Domestic pick, pack &amp; ship — no international Customs.",
  },
  {
    key: "shipping",
    overline: "Delivery window",
    display: "3–5",
    tail: "day shipping",
    sub: "Standard domestic. No three-week overseas waits.",
  },
  {
    key: "payments",
    overline: "Payments",
    display: null,       // renders chip row instead of a numeric callout
    chips: ["Credit Card", "Venmo", "Cash App"],
    sub: "Pay however works for your lab. Stripe, Venmo, or Cash App.",
  },
];

export default function LandingStatBox() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (paused) return;
    timer.current = setTimeout(() => setI((n) => (n + 1) % STATS.length), 5500);
    return () => clearTimeout(timer.current);
  }, [i, paused]);

  return (
    <div
      className="relative w-full max-w-[460px] mx-auto lg:mx-0"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="landing-stat-box"
    >
      {/* Offset back-panel for depth */}
      <div
        className="absolute inset-0 translate-x-3 translate-y-3 rounded-[6px] border border-borderSubtle bg-oxblood/[0.04]"
        aria-hidden
      />

      {/* Main card */}
      <div className="relative rounded-[6px] bg-white border border-borderSubtle p-6 sm:p-8 lg:p-10 min-h-[360px] sm:min-h-[400px] shadow-[0_30px_60px_-40px_rgba(26,26,26,0.20)] overflow-hidden flex flex-col">
        {/* Faint warm corner wash — keeps the box from looking clinical-cold */}
        <div
          className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full opacity-[0.35]"
          aria-hidden
          style={{
            background:
              "radial-gradient(circle, rgba(140,123,102,0.35), transparent 70%)",
          }}
        />
        {/* Brutalist cropmark corners */}
        <CornerMarks />

        {/* Content — all 4 slides mounted; opacity crossfade between them */}
        <div className="relative flex-1">
          {STATS.map((slide, idx) => (
            <div
              key={slide.key}
              aria-hidden={idx !== i}
              className={`absolute inset-0 ${idx === i ? "pointer-events-auto" : "pointer-events-none"}`}
              style={{
                opacity: idx === i ? 1 : 0,
                transition: "opacity 900ms cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <p
                className="text-[12px] uppercase tracking-[0.32em] text-oxblood font-mono"
                data-testid={idx === i ? "landing-stat-overline" : undefined}
              >
                {slide.overline}
              </p>

              <div className="mt-8 sm:mt-12">
                {slide.display && (
                  <p
                    className="font-serif text-charcoal leading-[0.95] tracking-tight text-[88px] sm:text-[112px]"
                    data-testid={idx === i ? "landing-stat-display" : undefined}
                  >
                    {slide.display}
                  </p>
                )}
                {slide.headline && !slide.display && (
                  <p
                    className="font-serif text-charcoal leading-[1.05] tracking-tight text-[44px] sm:text-[56px]"
                    data-testid={idx === i ? "landing-stat-headline" : undefined}
                  >
                    {slide.headline}
                  </p>
                )}
                {slide.tail && (
                  <p className="mt-2 font-serif italic text-2xl sm:text-3xl text-charcoal/80">
                    {slide.tail}
                  </p>
                )}

                {slide.chips && (
                  <div
                    className="flex flex-wrap gap-2 sm:gap-3 mt-2"
                    data-testid={idx === i ? "landing-stat-chips" : undefined}
                  >
                    {slide.chips.map((c) => (
                      <span
                        key={c}
                        className="inline-flex items-center px-4 py-3 rounded-full bg-bone border border-borderSubtle font-serif text-lg sm:text-xl text-charcoal"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Oxblood hairline underscore */}
              <div className="mt-8 h-[2px] w-14 bg-oxblood" aria-hidden />

              <p className="mt-4 text-sm sm:text-base text-stone leading-relaxed max-w-[38ch]">
                {stripAmp(slide.sub)}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom row — counter + pager dots */}
        <div className="relative mt-8 pt-5 border-t border-borderSubtle flex items-center justify-between">
          <p
            className="text-[12px] uppercase tracking-[0.32em] text-stone font-mono"
            data-testid="landing-stat-counter"
          >
            {String(i + 1).padStart(2, "0")} · {String(STATS.length).padStart(2, "0")}
          </p>
          <div className="flex items-center gap-2" role="tablist" aria-label="Highlight selector">
            {STATS.map((_, idx) => (
              <button
                key={idx}
                type="button"
                role="tab"
                aria-selected={idx === i}
                onClick={() => setI(idx)}
                className={`h-[6px] rounded-full transition-all duration-500 ${
                  idx === i
                    ? "w-8 bg-oxblood"
                    : "w-2 bg-charcoal/25 hover:bg-charcoal/50"
                }`}
                data-testid={`landing-stat-dot-${idx}`}
                aria-label={`Show highlight ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function stripAmp(s) {
  return typeof s === "string" ? s.replace(/&amp;/g, "&") : s;
}

function CornerMarks() {
  const stroke = "rgba(26,26,26,0.28)";
  const size = 14;
  const Mark = ({ style }) => (
    <span className="absolute" style={style} aria-hidden>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <path d={`M0 0 H${size} M0 0 V${size}`} stroke={stroke} strokeWidth="1" />
      </svg>
    </span>
  );
  return (
    <>
      <Mark style={{ top: 10, left: 10 }} />
      <Mark style={{ top: 10, right: 10, transform: "scaleX(-1)" }} />
      <Mark style={{ bottom: 10, left: 10, transform: "scaleY(-1)" }} />
      <Mark style={{ bottom: 10, right: 10, transform: "scale(-1,-1)" }} />
    </>
  );
}
